import { PureComponent } from 'react';
import classnames from 'classnames';
import { SavedSearch } from '@sbt-web/network/types';
import { Button } from '@sbt-web/ui';
import { FiltersConfigState } from '@/reducers/filtersConfig';
import type { ItemsState } from '@/reducers/items';
import type { SearchState } from '@/reducers/search';
import { SaveSearchProvider } from '@/shared/helpers/SavedSearches';
import {
  SavedSearchesIndex,
  buildIndex,
} from '@/shared/helpers/SavedSearches/matcher';
import { DefaultTrackingEvents } from '@/shared/helpers/SavedSearches/trackingEvents';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import {
  DisplayStrategy,
  ResizeStrategy,
  createDisplayStrategies as defaultCreateDisplayStrategies,
  createResizeStrategies as defaultCreateResizeStrategies,
} from './strategies';
import ownClasses from './FloatingButton.module.scss';

interface Props {
  search: SearchState;
  items: ItemsState;
  savedSearchesProvider: SaveSearchProvider;
  filtersConfig: FiltersConfigState;
}

interface State {
  show: boolean;
  resize: boolean;
  savedSearches: SavedSearch[];
  savedSearchesIndex: SavedSearchesIndex;
  firstFetchStarted: boolean;
  firstFetchDone: boolean;
  savedSearchButtonOutsideViewport: boolean;
}

class FloatingButton extends PureComponent<Props, State> {
  static displayName = 'FloatingButton';
  static TIMEOUT_SCROLL = 150;

  state;

  private displayStrategies: DisplayStrategy;
  private resizeStrategies: ResizeStrategy;
  private timeOutScroll: number | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      firstFetchStarted: false,
      firstFetchDone: false,
      show: false,
      resize: false,
      savedSearches: [],
      savedSearchButtonOutsideViewport: false,
      savedSearchesIndex: {},
    };

    this.displayStrategies = defaultCreateDisplayStrategies(this.state);

    this.resizeStrategies = defaultCreateResizeStrategies(
      this.state,
      this.setState.bind(this)
    );
  }

  async componentDidMount() {
    this.displayStrategies = defaultCreateDisplayStrategies(this.state);

    this.resizeStrategies = defaultCreateResizeStrategies(
      this.state,
      this.setState.bind(this)
    );

    window.addEventListener('scroll', this.onScroll, {
      passive: true,
    });

    window.addEventListener(
      'subito:saveSearch:saved',
      () => this.onSavedSearchFromAnotherEntryPoint(),
      {
        passive: true,
      }
    );

    await this.initSavedSearches();
  }

  onScroll = async () => {
    const handler = async () => {
      await this.shouldFloatingButtonBeResized();
    };
    if (this.timeOutScroll) {
      window.clearTimeout(this.timeOutScroll);
      this.timeOutScroll = null;
    }
    this.timeOutScroll = window.setTimeout(
      handler,
      FloatingButton.TIMEOUT_SCROLL
    );
    await this.checkFloatingButtonDisplayability();
  };

  onSavedSearchFromAnotherEntryPoint = async () => {
    const { savedSearchesProvider } = this.props;
    const savedSearches = await savedSearchesProvider.getSavedSearches();

    this.setState({
      savedSearches,
      savedSearchesIndex: buildIndex(savedSearches),
    });
  };

  initSavedSearches = async () => {
    const { savedSearchesProvider } = this.props;

    if (this.state.firstFetchStarted) {
      return this.state.savedSearches || [];
    }

    this.setState({ firstFetchStarted: true });

    const savedSearches = await savedSearchesProvider.getSavedSearches();

    const idx = buildIndex(savedSearches);

    this.setState({
      firstFetchDone: true,
      savedSearches,
      savedSearchesIndex: idx,
    });

    return savedSearches;
  };

  onNotifyMe = async () => {
    const { search, savedSearchesProvider, filtersConfig } = this.props;

    await savedSearchesProvider.saveSearch(search, filtersConfig);

    const savedSearches = await savedSearchesProvider.getSavedSearches();

    this.setState({
      savedSearches,
      savedSearchesIndex: buildIndex(savedSearches),
    });
  };

  async componentDidUpdate() {
    if (this.props.items.loading) {
      this.setState({ resize: false });
    }

    this.displayStrategies = defaultCreateDisplayStrategies(this.state);

    this.resizeStrategies = defaultCreateResizeStrategies(
      this.state,
      this.setState.bind(this)
    );

    if (this.state.firstFetchStarted) {
      await this.checkFloatingButtonDisplayability();
    } else {
      await this.initSavedSearches();
      await this.checkFloatingButtonDisplayability();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener(
      'subito:saveSearch:saved',
      this.onSavedSearchFromAnotherEntryPoint
    );
  }

  /**
   *  This method checks against the strategies if the snack bar should be displayed.
   *  Every strategy should evaluate to true in order to be able to display the snackbar
   *
   */
  checkFloatingButtonDisplayability = async () => {
    const { search, filtersConfig } = this.props;

    return await Promise.all(
      Object.values(this.displayStrategies).map((ds) =>
        ds(search, filtersConfig)
      )
    ).then((evaluations) => {
      const show = evaluations.every((result) => result);
      const prevShowState = this.state.show;
      this.setState({ show });
      return [show, prevShowState];
    });
  };

  shouldFloatingButtonBeResized = async () => {
    const { search, filtersConfig } = this.props;
    return await Promise.all(
      Object.values(this.resizeStrategies).map((ds) =>
        ds(search, filtersConfig)
      )
    )
      .then((evaluations) => {
        const resize = evaluations.every((result) => result);
        const prevResizeState = this.state.resize;
        this.setState({ resize });
        return [resize, prevResizeState];
      })
      .then(([shouldResize]) => {
        return shouldResize;
      });
  };

  get shouldFloatingButtonBeDisplayed() {
    return this.props.items.total > 0 && this.state.show;
  }

  render() {
    const loading = this.props.items.loading;

    return !loading ? (
      <Button
        classes={[
          ownClasses['floating-btn'],
          ownClasses['notify-me'],
          classnames({
            [ownClasses['visible']]: this.shouldFloatingButtonBeDisplayed,
            [ownClasses['floating-btn-resize-forward']]: this.state.resize,
            [ownClasses['floating-btn-resize-reverse']]: !this.state.resize,
          }),
        ]}
        size="large"
        onClick={() => {
          this.onNotifyMe();
        }}
        iconLeft={
          <img
            src="/static/img/ui/bell-large-on.svg"
            height="24"
            width="24"
            alt=""
            loading="lazy"
            decoding="async"
          />
        }
      >
        {this.state.resize ? undefined : 'Salva ricerca'}
      </Button>
    ) : null;
  }
}

class FloatingButtonTrackingEvents extends DefaultTrackingEvents {
  onBeforeSave() {
    getOrCreatePulse()?.queueEvent({
      type: 'Engagement',
      object: {
        id: 'save-search-banner-avvisami',
        type: 'UIElement',
        elementType: 'banner',
      },
      target: {
        id: '0000000000000000',
        type: 'Listing',
      },
      search: {
        '@id': '0000000000000000',
        '@type': 'SearchMetadata',
      },
    });
  }
}

export default FloatingButton;
export { FloatingButtonTrackingEvents };
export type { Props as FloatingButtonProps, State as FloatingButtonState };
